import { useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios from "../../utils/axios";
import {
  setError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";

function normalizeCountRows(rows = []) {
  if (!rows.length) {
    return {
      totalPages: 0,
      totalCount: 0,
    };
  }

  return {
    totalPages: rows[0]?.TotalPages || 0,
    totalCount: rows[0]?.TotalCount || 0,
  };
}

function normalizeAvailableBalances(rows = []) {
  const items = Array.isArray(rows) ? rows : [];
  const usdItem =
    items.find(
      (item) =>
        item?.Currency === "USD" ||
        item?.CurrencyId === 1 ||
        item?.CurrencyID === 1,
    ) || items[0] || {};
  const lbpItem =
    items.find(
      (item) =>
        item?.Currency === "LBP" ||
        item?.CurrencyId === 2 ||
        item?.CurrencyID === 2,
    ) || items[1] || {};

  return {
    USD: usdItem?.AvailableBalance ?? 0,
    LBP: lbpItem?.AvailableBalance ?? 0,
  };
}

export default function useNewSalesTransactionsAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const [salesTransactions, setSalesTransactions] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [totalNbOfPage, setTotalNbOfPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [branchesTillsData, setBranchesTillsData] = useState([]);
  const [availableBalances, setAvailableBalances] = useState({
    USD: 0,
    LBP: 0,
  });
  const [exportLoading, setExportLoading] = useState(false);

  async function GetSalesTransactions(
    currencyId,
    lastDate,
    dateFrom,
    dateTo,
    transactionType,
    branchName,
    tillName,
    isCustomBranch = false,
  ) {
    setSalesLoading(true);
    dispatch(setTableLoading({ key: "salesTransactions", loading: true }));
    dispatch(setError(null));

    try {
      const response = await client.post("/v1/Sales/GetSalesTransactions", {
        SessionID: sessionStorage.getItem("SessionID"),
        CurrencyID: currencyId,
        lastDate: lastDate || null,
        PageSize: 10,
        DateFrom: dateFrom,
        DateTo: dateTo,
        TransactionType: transactionType,
        BranchName: branchName || null,
        TillName: tillName || null,
        IsCustomBranch: isCustomBranch,
      });

      const rows = response?.data?.Payload?.SalesTransactions || [];
      const counts = normalizeCountRows(rows);

      setSalesTransactions(rows);
      setTotalNbOfPage(counts.totalPages);
      setTotalCount(counts.totalCount);
      dispatch(
        setTableData({
          key: "salesTransactions",
          data: rows,
          pagination: {
            lastDate: lastDate || null,
            totalPages: counts.totalPages,
            totalCount: counts.totalCount,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "forms",
          key: "salesTransactions",
          value: {
            currencyId,
            dateFrom,
            dateTo,
            transactionType,
            branchName,
            tillName,
            isCustomBranch,
          },
        }),
      );

      return rows;
    } catch (error) {
      dispatch(
        setTableData({
          key: "salesTransactions",
          data: [],
          pagination: {
            lastDate: lastDate || null,
            totalPages: 0,
            totalCount: 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "Unable to load sales transactions.",
        ),
      );
      throw error;
    } finally {
      setSalesLoading(false);
      dispatch(setTableLoading({ key: "salesTransactions", loading: false }));
    }
  }

  async function GetSalesTransactionsExport(
    currencyId,
    dateFrom,
    dateTo,
    transactionType,
  ) {
    setExportLoading(true);

    try {
      const response = await client.post("/v1/Sales/GetSalesTransactions", {
        SessionID: sessionStorage.getItem("SessionID"),
        CurrencyID: currencyId,
        page: 1,
        pageSize: 1,
        DateFrom: dateFrom,
        DateTo: dateTo,
        TransactionType: transactionType,
        isExport: true,
      });

      return response?.data?.Payload?.SalesTransactions || [];
    } finally {
      setExportLoading(false);
    }
  }

  async function GetAvailableSalesAndBalance() {
    const response = await client.post("/v1/Sales/GetAvailableSalesAndBalance", {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    const balances = normalizeAvailableBalances(
      response?.data?.Payload?.AvailableSalesBalances || [],
    );
    setAvailableBalances(balances);
    dispatch(
      setFeatureData({
        section: "summary",
        key: "salesTransactions",
        value: {
          availableBalances: balances,
        },
      }),
    );
    return balances;
  }

  async function GetBranchesTillsByMerchant() {
    const response = await client.post("/v1/Sales/GetBranchesTillsByMerchant", {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    const rows = response?.data?.Payload?.BranchesTills || [];
    setBranchesTillsData(rows);
    dispatch(
      setFeatureData({
        section: "details",
        key: "salesTransactions",
        value: {
          branchesTills: rows,
        },
      }),
    );
    return rows;
  }

  return {
    GetSalesTransactions,
    GetSalesTransactionsExport,
    GetAvailableSalesAndBalance,
    GetBranchesTillsByMerchant,
    salesTransactions,
    salesLoading,
    totalNbOfPage,
    totalCount,
    branchesTillsData,
    availableBalances,
    exportLoading,
  };
}
